/**
 * The client half of shell sync (ALU-168).
 *
 * The shell (classic DashboardShell or EncuentroFrame) and the `data-ui` on
 * `<html>` come from layouts, which a client navigation doesn't re-render.
 * When the global switch flips (or rolls back) while a tab or the native app
 * is open, the next navigation would put the other interface's page inside
 * the old shell, unstyled, until a reload.
 *
 * proxy.ts stamps UI_VERSION_HEADER on every router request (proxy-hint.ts).
 * This module wraps `fetch` (Next's router and server actions call the global
 * at call time) and compares the header with the shell's version:
 *
 * - navigation or refresh: a full load of the target URL instead of the soft
 *   navigation. The router's promise is held, so the old page stays on screen
 *   until the browser leaves it; nothing of the other interface is painted.
 *   The person chose to leave the page, so this is the safe point.
 * - server action: the action already ran. Many run in the background with
 *   nobody asking (the unread counter on focus, Empleos), so an action never
 *   throws away what the person is doing (bug bash SW-1, decideShellSync):
 *   - with unsent text or an upload in flight (lib/utils/unsent-work.ts): no reload.
 *     The change waits for the next navigation and the "new version" notice
 *     offers to update now (components/ui-shell-update-notice.tsx);
 *   - an action that brings a new page (it revalidated or redirected): a full
 *     reload of the current page, as that page is the other interface's;
 *   - an action that brings nothing (a count, a list fetched in the
 *     background): no reload; the next navigation does it.
 * - prefetch: nothing loads yet. The mismatch is remembered and the next
 *   navigation (onNavigationStart, wired to onRouterTransitionStart) becomes
 *   a full load, so a prefetched loading state never paints either.
 * - resume (WebDeploySync, the native app back from the background): a full
 *   load, unless there is unsent work; then the same notice.
 *
 * Loop guard: one full load per target version per SHELL_RELOAD_COOLDOWN_MS
 * per tab (sessionStorage). A reload is only ever triggered by a navigation
 * or an action, never on page load, so it cannot loop by itself; the guard
 * also stops a stale hint (the 60 s render cache after a manual SQL change)
 * from turning every click into a full load. A load whose shell matches the
 * mark clears it, so a later rollback reloads again.
 *
 * The shell is the root layout's stamp (SHELL_VERSION_ATTRIBUTE), never the
 * live `data-ui`, which a page may set itself (ALU-304).
 *
 * A deferred change is kept on `window` (pendingShellUpdate), so the next
 * navigation applies it and the notice, mounted by the root layout, can show
 * it whichever bundle copy of this module noticed it.
 *
 * Works the same in the Capacitor WebView (same bundle, same router).
 * WebDeploySync also checks the header on its resume probe.
 */
import {
  safeGetSessionItem,
  safeRemoveSessionItem,
  safeSetSessionItem,
} from '@/lib/utils/safe-storage'
import { ENCUENTRO_SCOPE_ATTRIBUTE, ENCUENTRO_SCOPE_VALUE } from '@/lib/design/encuentro-scope'
import { parseUiVersion, type UiVersion } from './resolve'
import { hasUnsentWork, installUploadTracking } from '@/lib/utils/unsent-work'

export const UI_VERSION_HEADER = 'x-alumnia-ui-version'
export const SHELL_RELOAD_KEY = 'alumnia:ui-shell-reload'
export const SHELL_RELOAD_COOLDOWN_MS = 60_000
/** How long a router promise is held after asking for a full load, in case the browser stays (a beforeunload prompt the user cancels). */
export const SHELL_RELOAD_HOLD_MS = 4_000

/** Next's cache-busting param on router requests (NEXT_RSC_UNION_QUERY). */
const RSC_MARKER_PARAM = '_rsc'

export type RouterRequestKind = 'navigation' | 'prefetch' | 'action'
export type ShellReloadTrigger = RouterRequestKind | 'resume'

/** Which kind of Next router request these headers belong to, if any. */
export function routerRequestKind(headers: Headers): RouterRequestKind | null {
  if (headers.has('next-action')) return 'action'
  if (headers.has('next-router-prefetch') || headers.has('next-router-segment-prefetch')) {
    return 'prefetch'
  }
  if (headers.has('rsc')) return 'navigation'
  return null
}

/**
 * Stamped on `<html>` by the root layout with the version it rendered, and
 * written by nothing else. `data-ui` is not enough: a page that follows the
 * public site instead of the switch (`/start`, `/start/confirm`, the
 * marketing pages) puts the Encuentro scope on `<html>` itself after
 * hydration (HtmlScope), in classic too. Read as the shell, that made every
 * server action on those pages look like a flip, and the reload it caused
 * landed on a document whose `data-ui` was classic again until HtmlScope ran,
 * which cleared the loop guard's mark: an endless reload (ALU-304).
 */
export const SHELL_VERSION_ATTRIBUTE = 'data-ui-shell'

/**
 * The version the current document's shell was rendered with: the root
 * layout's stamp, or `data-ui` for a document without one (a tab from an
 * older build, the global error page).
 */
export function shellVersionOf(doc: Pick<Document, 'documentElement'>): UiVersion {
  const stamped = parseUiVersion(doc.documentElement.getAttribute(SHELL_VERSION_ATTRIBUTE))
  if (stamped) return stamped
  return doc.documentElement.getAttribute(ENCUENTRO_SCOPE_ATTRIBUTE) === ENCUENTRO_SCOPE_VALUE
    ? 'encuentro'
    : 'classic'
}

/** The page URL behind a router request URL (drops Next's `_rsc` marker). */
export function pageUrlOf(requestUrl: string, base: string): string {
  const url = new URL(requestUrl, base)
  url.searchParams.delete(RSC_MARKER_PARAM)
  return url.toString()
}

type ReloadMark = { to: UiVersion; at: number }

export function parseReloadMark(raw: string | null): ReloadMark | null {
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) return null
    const { to, at } = value as { to?: unknown; at?: unknown }
    const version = parseUiVersion(to)
    return version && typeof at === 'number' ? { to: version, at } : null
  } catch {
    return null
  }
}

/** The loop guard: no second full load toward the same version within the cooldown. */
export function mayReloadToward(mark: ReloadMark | null, to: UiVersion, now: number): boolean {
  return !mark || mark.to !== to || now - mark.at >= SHELL_RELOAD_COOLDOWN_MS
}

type ShellWindow = Pick<Window, 'location' | 'document' | 'setTimeout'> & {
  fetch: typeof fetch
  XMLHttpRequest?: typeof XMLHttpRequest
  dispatchEvent?: Window['dispatchEvent']
}

/** Next's revalidation kinds (ActionDidRevalidateStaticAndDynamic, …DynamicOnly). */
const ACTION_REVALIDATED_KINDS = new Set([1, 2])

/**
 * Whether a server action's response carries a new page: it redirected, or
 * revalidated (then Next renders the tree again from the response). One that
 * only returned data (a count, a list) leaves the page as it is.
 */
export function actionBringsNewPage(headers: Headers): boolean {
  if (headers.get('x-action-redirect')) return true
  const revalidated = headers.get('x-action-revalidated')
  if (!revalidated) return false
  try {
    return ACTION_REVALIDATED_KINDS.has(JSON.parse(revalidated) as number)
  } catch {
    return false
  }
}

/**
 * What to do when the server renders the other interface (bug bash SW-1):
 * - 'reload': a full load now (the loop guard still applies);
 * - 'defer': nothing now, the next navigation loads;
 * - 'notify': nothing now, the next navigation loads, and the person sees
 *   the "new version" notice with an Update button.
 * Never 'reload' over unsent work unless the person is navigating away.
 */
export type ShellSyncDecision = 'reload' | 'defer' | 'notify'

export function decideShellSync({
  trigger,
  bringsNewPage = false,
  unsentWork,
}: {
  trigger: ShellReloadTrigger
  bringsNewPage?: boolean
  unsentWork: boolean
}): ShellSyncDecision {
  switch (trigger) {
    case 'navigation':
    case 'prefetch':
      // Leaving the page: the soft navigation would unmount it all the same.
      return 'reload'
    case 'action':
      if (unsentWork) return 'notify'
      return bringsNewPage ? 'reload' : 'defer'
    case 'resume':
      return unsentWork ? 'notify' : 'reload'
  }
}

// ---------------------------------------------------------------------------
// The deferred change
// ---------------------------------------------------------------------------

/** Fired on `window` when the pending change (and whether to show the notice) changes. */
export const SHELL_UPDATE_EVENT = 'alumnia:ui-shell-update'
const PENDING_KEY = '__alumniaShellUpdate'

export type PendingShellUpdate = { to: UiVersion; notify: boolean }

type PendingWindow = { [PENDING_KEY]?: PendingShellUpdate | null }

export function pendingShellUpdate(win: object): PendingShellUpdate | null {
  return (win as PendingWindow)[PENDING_KEY] ?? null
}

/**
 * Remember (or forget, with null) the version the server renders now. Once
 * the notice is shown it stays until the version changes or the page loads.
 */
export function setPendingShellUpdate(
  win: Pick<ShellWindow, 'dispatchEvent'>,
  next: PendingShellUpdate | null
): void {
  const current = pendingShellUpdate(win)
  const merged =
    next && current && current.to === next.to
      ? { to: next.to, notify: current.notify || next.notify }
      : next
  if (current?.to === merged?.to && current?.notify === merged?.notify) return
  ;(win as PendingWindow)[PENDING_KEY] = merged
  try {
    win.dispatchEvent?.(new CustomEvent(SHELL_UPDATE_EVENT, { detail: merged }))
  } catch {
    // An old WebView without CustomEvent: the next navigation still loads.
  }
}

/**
 * The notice's Update button: the person asked, so it loads now (the loop
 * guard's mark is written so the landing clears it like any other reload).
 */
export function applyShellUpdateNow(
  win: Pick<ShellWindow, 'location' | 'dispatchEvent'>,
  now: number = Date.now()
): void {
  const pending = pendingShellUpdate(win)
  if (pending) safeSetSessionItem(SHELL_RELOAD_KEY, JSON.stringify({ to: pending.to, at: now }))
  win.location.reload()
}

export type ShellReloadEvent = {
  trigger: ShellReloadTrigger
  from: UiVersion
  to: UiVersion
}

type Options = {
  onReload?: (event: ShellReloadEvent) => void
  now?: () => number
}

/**
 * Full load of `target` when `serverVersion` differs from this document's
 * shell and the guard allows it. Returns whether a load was started. Callers
 * that may run in the background go through syncShellTo, which asks
 * decideShellSync first.
 */
export function reloadIfShellStale(
  win: Pick<ShellWindow, 'location' | 'document'>,
  target: string,
  serverVersion: UiVersion | null,
  trigger: ShellReloadTrigger,
  { onReload, now = Date.now }: Options = {}
): boolean {
  if (!serverVersion) return false
  const shell = shellVersionOf(win.document)
  if (serverVersion === shell) return false
  const at = now()
  if (!mayReloadToward(parseReloadMark(safeGetSessionItem(SHELL_RELOAD_KEY)), serverVersion, at)) {
    return false
  }
  safeSetSessionItem(SHELL_RELOAD_KEY, JSON.stringify({ to: serverVersion, at }))
  try {
    onReload?.({ trigger, from: shell, to: serverVersion })
  } catch {
    // Telemetry never blocks the reload.
  }
  if (target === win.location.href) win.location.reload()
  else win.location.assign(target)
  return true
}

/**
 * The whole rule for one signal that the server renders `serverVersion`:
 * nothing when it is this shell's (and a stale pending change is dropped);
 * otherwise decideShellSync says whether to load now or leave it pending,
 * with or without the notice. Returns whether a load was started.
 */
export function syncShellTo(
  win: Pick<ShellWindow, 'location' | 'document' | 'dispatchEvent'>,
  target: string,
  serverVersion: UiVersion | null,
  trigger: ShellReloadTrigger,
  options: Options & { bringsNewPage?: boolean } = {}
): boolean {
  if (!serverVersion) return false
  if (serverVersion === shellVersionOf(win.document)) {
    // The server agrees with this shell again (a flip and its rollback).
    setPendingShellUpdate(win, null)
    return false
  }
  const decision = decideShellSync({
    trigger,
    bringsNewPage: options.bringsNewPage,
    unsentWork: hasUnsentWork(win),
  })
  if (decision === 'reload' && reloadIfShellStale(win, target, serverVersion, trigger, options)) {
    return true
  }
  setPendingShellUpdate(win, { to: serverVersion, notify: decision === 'notify' })
  return false
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  if (init?.headers) return new Headers(init.headers)
  if (typeof Request !== 'undefined' && input instanceof Request) return input.headers
  return new Headers()
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

/**
 * Wraps `win.fetch` once (and counts XMLHttpRequest uploads). Returns the
 * hook for Next's onRouterTransitionStart.
 */
export function installUiShellSync(win: ShellWindow, options: Options = {}) {
  // A load whose shell is the version we reloaded toward worked: clear the
  // mark so a later flip (or rollback) can reload again.
  const mark = parseReloadMark(safeGetSessionItem(SHELL_RELOAD_KEY))
  if (mark && mark.to === shellVersionOf(win.document)) safeRemoveSessionItem(SHELL_RELOAD_KEY)
  // A new document: whatever was pending belonged to the one before.
  ;(win as PendingWindow)[PENDING_KEY] = null
  // Uploads in flight are counted by installUploadTracking (idempotent; also
  // called from instrumentation-client.ts, so the count outlives this module).
  installUploadTracking(win)

  const originalFetch = win.fetch

  const hold = (response: Response) =>
    new Promise<Response>((resolve) => {
      win.setTimeout(() => resolve(response), SHELL_RELOAD_HOLD_MS)
    })

  win.fetch = async function shellSyncFetch(input: RequestInfo | URL, init?: RequestInit) {
    // Uploads are counted one level down, by installUploadTracking.
    const response = await originalFetch.call(win, input, init)
    let kind: RouterRequestKind | null = null
    try {
      kind = routerRequestKind(requestHeaders(input, init))
      if (!kind) return response
      const serverVersion = parseUiVersion(response.headers.get(UI_VERSION_HEADER))
      if (!serverVersion) return response
      if (kind === 'prefetch') {
        // Nothing loads on a prefetch: remembered for the next navigation.
        if (serverVersion === shellVersionOf(win.document)) setPendingShellUpdate(win, null)
        else setPendingShellUpdate(win, { to: serverVersion, notify: false })
        return response
      }
      const target =
        kind === 'action'
          ? win.location.href
          : pageUrlOf(response.url || requestUrl(input), win.location.href)
      const started = syncShellTo(win, target, serverVersion, kind, {
        ...options,
        bringsNewPage: kind === 'action' && actionBringsNewPage(response.headers),
      })
      if (started) return hold(response)
    } catch {
      // Never break a request over the check.
    }
    return response
  } as typeof fetch

  return {
    /** Next's onRouterTransitionStart: a prefetch, an action or the app's resume already saw the other version. */
    onNavigationStart(url: string) {
      const pending = pendingShellUpdate(win)
      if (!pending) return
      try {
        syncShellTo(win, new URL(url, win.location.href).toString(), pending.to, 'navigation', options)
      } catch {
        // Never break a navigation over the check.
      }
    },
  }
}
